import React, { useState } from 'react';
import axios from 'axios';
import { Formik, useFormikContext } from 'formik';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import colors from '~/assets/colors.module.scss';
import CardDialog from '~/components/CardDialog';
import { Heading, SortableTable, Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import IconButton from '~/components/core/Atomic/Buttons/IconButton';
import CancelButton from '~/components/core/Buttons/CancelButton';
import RadioButtonGroupFormik from '~/components/core/Formik/RadioButtonGroupFormik';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import { PencilIcon, PlusIcon } from '~/components/icons';
import { useCliveConfigOptions } from '~/components/SystemConfiguration/CliveConfiguration/CliveConfigurationContext';
import FieldConfigurationCard from '~/components/SystemConfiguration/FnolConfiguration/FnolConfigurationScreen/FieldConfigurationCard/FieldConfigurationCard';
import OperationsBreadcrumbs from '~/components/SystemConfiguration/OperationsBreadcrumbs';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import type { OrganizationModel } from '~/components/types/organization-types';
import useDataFetcher from '~/components/useDataFetcher';
import type { InvoiceConfigInfo } from '~/server_shared/openapi/data-contracts';
import { InvoiceConfigInfoCliveCoverageClassificationLevel } from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

const INVOICE_PREDEFINED_FIELD_IDS = [
  'document',
  'item_date',
  'amount',
  'description',
  'amount_currency',
  'third_party_amount',
];

const DEFAULT_CONFIG = {
  entity_granularity: 'line_item' as const,
  clive_coverage_classification: InvoiceConfigInfoCliveCoverageClassificationLevel.None,
  predefined_fields: {
    document: { desc: 'Document', active: true, mandatory: false, type: 'string', dynamic: true },
    item_date: { desc: 'Date', active: true, mandatory: false, type: 'date', dynamic: true },
    amount: { desc: 'Amount', active: true, mandatory: false, type: 'monetary', dynamic: true },
    description: { desc: 'Description', active: true, mandatory: false, type: 'string', dynamic: true },
    amount_currency: { desc: 'Currency', active: true, mandatory: false, type: 'string', dynamic: true },
    third_party_amount: {
      desc: 'Third Party Amount',
      active: false,
      mandatory: false,
      type: 'monetary',
      dynamic: true,
    },
  },
};

const ENTITY_GRANULARITY_OPTIONS = [
  { optionValue: 'line_item', text: 'Line Item' },
  { optionValue: 'invoice', text: 'Invoice Summary' },
];

const ENTITY_GRANULARITY_LABELS: Record<string, string> = {
  line_item: 'Line Item',
  invoice: 'Invoice Summary',
};

const CLIVE_COVERAGE_CLASSIFICATION_OPTIONS = [
  { optionValue: InvoiceConfigInfoCliveCoverageClassificationLevel.None, text: 'None' },
  { optionValue: InvoiceConfigInfoCliveCoverageClassificationLevel.AssignCoverage, text: 'Assign coverage only' },
  {
    optionValue: InvoiceConfigInfoCliveCoverageClassificationLevel.FullClassification,
    text: 'Assign coverage and decision',
  },
];

type InvoiceConfigResponse = InvoiceConfigInfo;

// ── Edit Dialog (inner form) ──

const UpsertInvoiceConfigFormInner: React.FC = () => {
  const { isSubmitting } = useFormikContext();
  const { isCliveEnabledForOrganization } = useCliveConfigOptions();
  const showCliveSection = isCliveEnabledForOrganization();

  return (
    <div className="grid gap-20">
      <div>
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
          Entry Granularity
        </Text>
        <RadioButtonGroupFormik
          id="config.entity_granularity"
          options={ENTITY_GRANULARITY_OPTIONS}
          showOnly={isSubmitting}
        />
      </div>
      {showCliveSection ? (
        <div>
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
            Clive Coverage Classification
          </Text>
          <RadioButtonGroupFormik
            id="config.clive_coverage_classification"
            options={CLIVE_COVERAGE_CLASSIFICATION_OPTIONS}
            showOnly={isSubmitting}
          />
        </div>
      ) : null}
      <div>
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
          Invoice Predefined Fields
        </Text>
        {INVOICE_PREDEFINED_FIELD_IDS.map((fieldId) => (
          <FieldConfigurationCard
            key={fieldId}
            idPrefix="config.predefined_fields"
            fieldId={fieldId}
            disableType
            disableCategories
            withoutRequiredForClaimClosing
            withRequiredConfirmationModal={false}
          />
        ))}
      </div>
    </div>
  );
};

interface UpsertInvoiceConfigDialogProps {
  invoiceConfig?: InvoiceConfigResponse;
  onClose: () => void;
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
}

const UpsertInvoiceConfigDialog: React.FC<UpsertInvoiceConfigDialogProps> = ({ invoiceConfig, onClose, onSubmit }) => {
  const initialConfig = invoiceConfig?.config
    ? {
        ...DEFAULT_CONFIG,
        ...invoiceConfig.config,
        predefined_fields: {
          ...DEFAULT_CONFIG.predefined_fields,
          ...invoiceConfig.config.predefined_fields,
        },
      }
    : DEFAULT_CONFIG;

  return (
    <Formik
      initialValues={{ config: initialConfig }}
      enableReinitialize
      onSubmit={async (values, formikProps) => {
        try {
          await onSubmit(values);
          onClose();
        } catch (error) {
          formikProps.setSubmitting(false);
          await reportAxiosError(error);
        }
      }}
    >
      {({ isSubmitting, handleSubmit }) => (
        <CardDialog
          title={invoiceConfig ? 'Edit Invoice Configuration' : 'Create Invoice Configuration'}
          isDialog
          onClose={onClose}
          fullWidth
          maxWidth="lg"
        >
          <UpsertInvoiceConfigFormInner />
          <div className="mt-32 flex w-full justify-end">
            <CancelButton disabled={isSubmitting} onClick={onClose} />
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <Button variant="contained" color="primary" disabled={isSubmitting} onClick={handleSubmit}>
              Save
            </Button>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

// ── Main Screen (table + dialog) ──

const InvoiceConfigurationScreen: React.FC = () => {
  const { organization } = useSysconfig() as { organization: OrganizationModel };
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const baseRoute = `/sysconfig/api/v1/organizations/${organization.id}/invoice_config`;
  const {
    isLoading,
    isError,
    data: invoiceConfig = null,
    reloadData,
  } = useDataFetcher<InvoiceConfigInfo | null>(baseRoute);

  const handleCreate = async (values: Record<string, unknown>) => {
    await axios.post(baseRoute, {
      display_name: 'Invoice Configuration',
      ...values,
    });
    await reloadData();
  };

  const handleUpdate = async (values: Record<string, unknown>) => {
    await axios.patch(baseRoute, values);
    await reloadData();
  };

  const displayLoading = isLoading || isError;

  const columns = [
    { id: 'display_name', label: 'Name' },
    {
      id: 'scope',
      label: 'Scope',
      specialCell: () => 'Org-wide',
    },
    {
      id: 'entity_granularity',
      label: 'Entry Granularity',
      specialCell: (row: InvoiceConfigResponse) => {
        const granularity = row.config?.entity_granularity;
        return (granularity && ENTITY_GRANULARITY_LABELS[granularity]) || granularity || '-';
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      specialCell: () => (
        <IconButton onClick={() => setIsEditOpen(true)}>
          <PencilIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <CardDialog noCardTitle>
      <OperationsBreadcrumbs currentTab="Invoice Configuration" />
      <div className="grid grid-cols-2 items-center">
        <Heading className="mt-20">Invoice Configuration</Heading>
        {!invoiceConfig && !displayLoading && (
          <Button
            className="justify-self-end text-teal-700"
            onClick={() => setIsCreateOpen(true)}
            startIcon={<PlusIcon iconColor={colors.buttonLink} size={14} />}
          >
            Add Invoice Configuration
          </Button>
        )}
      </div>
      {displayLoading ? (
        <SkeletonTable rowsCount={1} columnsCount={5} isError={isError} />
      ) : (
        <SortableTable columns={columns} rows={invoiceConfig ? [invoiceConfig] : []} />
      )}
      {isEditOpen && invoiceConfig ? (
        <UpsertInvoiceConfigDialog
          invoiceConfig={invoiceConfig}
          onSubmit={handleUpdate}
          onClose={() => setIsEditOpen(false)}
        />
      ) : null}
      {isCreateOpen ? (
        <UpsertInvoiceConfigDialog onSubmit={handleCreate} onClose={() => setIsCreateOpen(false)} />
      ) : null}
    </CardDialog>
  );
};

export default InvoiceConfigurationScreen;
